/** @prettier */

/**
 * サイト一覧画面のサブメニュー
 */

import {
  Grid,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Box,
  DialogTitle,
  Dialog,
  DialogContent,
  List,
  DialogActions,
  ListItem,
  ListItemText,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

//MTH component import
import TextFieldSelectBox from '../../component/SelectBox';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {patchSettingUser} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';
/* TypeDefine import */
import * as type from '../../Type/TypeDef';

import {pullDownMenu, iconBlack, iconWhite, iconRed} from '../../define';

export const MainSiteSubHeader = (props: {
  filterSetting: type.filterSettingProps.filterSettingData;
  setSelectedPage: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const trans = useTranslation().t; //翻訳関数

  //prettier-ignore
  /** ソート順 */
  const [tempDisplayOrder, setTempDisplayOrder] = useState(props.filterSetting.displayOrder);
  //prettier-ignore
  /** 1ページあたりの表示数 */
  const [tempDisplayedInPage, setTempDisplayedInPage] = useState(props.filterSetting.displayedInPage);
  //prettier-ignore
  /** 機器フィルター */
  const [tempDeviceFilter, setTempDeviceFilter] = useState(props.filterSetting.deviceFilter);
  //prettier-ignore
  /** 権限フィルター */
  const [tempAuthorityFilter, setTempAuthorityFilter] = useState(props.filterSetting.authorityFilter);
  //ダイアログ開閉フラグ
  const [filterSettingOpen, setFilterSettingOpen] = useState(false);

  //チップ表示用
  //機器フィルター
  const [deviceFilterDisplay, setDeviceFilterDisplay] =
    useState<type.filterSettingProps.filterSettingData['deviceFilter']>(
      'unfiltered'
    );
  //権限フィルター
  const [authorityFilterDisplay, setAuthorityFilterDisplay] =
    useState<type.filterSettingProps.filterSettingData['authorityFilter']>(
      'unfiltered'
    );

  //画面遷移時に初期化しないのでレンダリング時に初期化
  useEffect(() => {
    props.filterSetting.setDisplayOrder('registeredDate');
    props.filterSetting.setDisplayedInPage('10');
    props.filterSetting.setDeviceFilter('unfiltered');
    props.filterSetting.setAuthorityFilter('unfiltered');
  }, []);

  /** 機器フィルター画面表示用文字列変換テーブル */
  const deviceFilterConvertTable: {
    [key in 'air-conditioner' | 'freezer' | 'qton']: string;
  } = {
    'air-conditioner': trans('MainSiteSubHeader.filterText.airConditioner'),
    'freezer': trans('MainSiteSubHeader.filterText.hccvqChiller'),
    'qton': trans('MainSiteSubHeader.filterText.qton'),
  };

  /** 権限フィルター画面表示用文字列変換テーブル */
  const authorityFilterConvertTable: {
    [key in 'admin' | 'manager' | 'viewer']: string;
  } = {
    admin: trans('MainSiteSubHeader.filterText.admin'),
    manager: trans('MainSiteSubHeader.filterText.manager'),
    viewer: trans('MainSiteSubHeader.filterText.viewer'),
  };

  /** フィルターアイコン押下時処理 */
  const settingButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterSettingOpen(true);
    // 現在の設定を一時変数にコピー
    setTempDisplayOrder(props.filterSetting.displayOrder);
    setTempDisplayedInPage(props.filterSetting.displayedInPage);
    setTempDeviceFilter(props.filterSetting.deviceFilter);
    setTempAuthorityFilter(props.filterSetting.authorityFilter);
  };

  /* Filter Setting OKボタン押下時 */
  const filterApplied = async () => {
    // 画面表示更新
    setDeviceFilterDisplay(tempDeviceFilter);
    setAuthorityFilterDisplay(tempAuthorityFilter);

    // 修正内容セット
    const query: apiIF.reqParameters.patchSettingUser = {
      'site-display': {
        'max-result': tempDisplayedInPage,
        'sort': tempDisplayOrder,
      },
    };
    // ユーザ情報（サイト一覧表示）の修正
    await patchSettingUser(query);
    // ページを強制的に1ページ目に移動
    props.setSelectedPage(1);
    // フィルター内容を反映
    props.filterSetting.setDisplayOrder(tempDisplayOrder);
    props.filterSetting.setDisplayedInPage(tempDisplayedInPage);
    props.filterSetting.setDeviceFilter(tempDeviceFilter);
    props.filterSetting.setAuthorityFilter(tempAuthorityFilter);
    // ダイアログを閉じる
    setFilterSettingOpen(false);
  };

  /* Filter Setting キャンセル時 */
  const handleClose = () => {
    // ダイアログを閉じる
    setFilterSettingOpen(false);
  };

  // 機器フィルターの×ボタンによる解除時処理
  const deviceFilterClose = async () => {
    //機器フィルター情報の非表示
    setDeviceFilterDisplay('unfiltered');
    // 機器フィルター解除
    props.filterSetting.setDeviceFilter('unfiltered');
    // ページを強制的に1ページ目に移動
    props.setSelectedPage(1);
  };

  // 権限フィルターの×ボタンによる解除時処理
  const authorityFilterClose = async () => {
    //権限フィルター情報の非表示
    setAuthorityFilterDisplay('unfiltered');
    // 権限フィルター解除
    props.filterSetting.setAuthorityFilter('unfiltered');
    // ページを強制的に1ページ目に移動
    props.setSelectedPage(1);
  };

  /** デバイスフィルター　アイコン選択時 */
  const selectDeviceFilter = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    switch (newAlignment) {
      case 'air-conditioner':
      case 'freezer':
      case 'qton':
        setTempDeviceFilter(newAlignment);
        break;
      //既定の機種以外の場合はフィルターなし設定
      default:
        setTempDeviceFilter('unfiltered');
    }
  };

  return (
    <>
      <Grid
        container
        className="mySiteMainContents__subHeader--siteList"
        justifyContent="flex-end"
        alignItems="center"
        columnSpacing={1}>
        {/* 権限フィルターがある場合は表示 */}
        {authorityFilterDisplay !== 'unfiltered' ? (
          <Grid item xs="auto">
            <Chip
              deleteIcon={
                <img
                  src={iconRed.CLOSE.src}
                  alt={`${trans(
                    `MainSiteSubHeader.image.alternativeText.${iconRed.CLOSE.alt}`
                  )}`}
                  className="mySiteMainContents__subHeader--siteList__filterChip__icon"
                />
              }
              label={
                <Box className="mySiteMainContents__subHeader--siteList__filterChip__text">
                  {authorityFilterConvertTable[authorityFilterDisplay]}
                </Box>
              }
              onDelete={authorityFilterClose}
              variant="outlined"
            />
          </Grid>
        ) : (
          <></>
        )}
        {/* 機器フィルターがある場合は表示 */}
        {deviceFilterDisplay !== 'unfiltered' ? (
          <Grid item xs="auto">
            <Chip
              deleteIcon={
                <img
                  src={iconRed.CLOSE.src}
                  alt={`${trans(
                    `MainSiteSubHeader.image.alternativeText.${iconRed.CLOSE.alt}`
                  )}`}
                  className="mySiteMainContents__subHeader--siteList__filterChip__icon"
                />
              }
              label={
                <Box className="mySiteMainContents__subHeader--siteList__filterChip__text">
                  {deviceFilterConvertTable[deviceFilterDisplay]}
                </Box>
              }
              onDelete={deviceFilterClose}
              variant="outlined"
            />
          </Grid>
        ) : (
          <></>
        )}
        <Grid item xs="auto">
          {/* フィルター設定ボタン */}
          <IconButton
            onClick={settingButton}
            className="mySiteMainContents__subHeader--siteList__filterSettingButton">
            <img
              src={iconBlack.FILTER.src}
              alt={`${trans(
                `MainSiteSubHeader.image.alternativeText.${iconBlack.FILTER.alt}`
              )}`}
              title={`${trans(
                `MainSiteSubHeader.image.title.${iconBlack.FILTER.title}`
              )}`}
              className="mySiteMainContents__subHeader--siteList__filterSettingButton__image"
            />
          </IconButton>
        </Grid>
      </Grid>
      <Dialog onClose={handleClose} open={filterSettingOpen} maxWidth="md">
        <DialogTitle className="mySiteMainContents__subHeader--siteList__filterSettingDialog__title">
          {trans('MainSiteSubHeader.dialog.title')}
        </DialogTitle>
        <DialogContent>
          <List>
            {/* ソート順 */}
            <ListItem>
              <ListItemText
                primary={
                  <Box className="mySiteMainContents__subHeader--siteList__filterSettingDialog__item__title">
                    {trans('MainSiteSubHeader.dialog.contents.sort')}
                  </Box>
                }
              />
            </ListItem>
            <ListItem>
              <TextFieldSelectBox
                choices={pullDownMenu.SITE_DISPLAY_ORDER}
                setValue={setTempDisplayOrder}
                default={tempDisplayOrder}
                class="mySiteMainContents__subHeader--siteList__filterSettingDialog__item__selectBox--sortBy"
                id="selectSiteDisplayOrder"
              />
            </ListItem>
            {/* サイト表示数 */}
            <ListItem>
              <ListItemText
                primary={
                  <Box className="mySiteMainContents__subHeader--siteList__filterSettingDialog__item__title">
                    {trans('MainSiteSubHeader.dialog.contents.sitesPerPage')}
                  </Box>
                }
              />
            </ListItem>
            <ListItem>
              <TextFieldSelectBox
                choices={pullDownMenu.SITE_DISPLAYED_IN_PAGE}
                setValue={setTempDisplayedInPage}
                default={tempDisplayedInPage}
                class="mySiteMainContents__subHeader--siteList__filterSettingDialog__item__selectBox--sitePerPage"
                id="selectSiteDisplayedInPage"
              />
            </ListItem>
            {/* 機器フィルター */}
            <ListItem>
              <ListItemText
                primary={
                  <Box className="mySiteMainContents__subHeader--siteList__filterSettingDialog__item__title">
                    {trans(
                      'MainSiteSubHeader.dialog.contents.filterDeviceType'
                    )}
                  </Box>
                }
              />
            </ListItem>
            <ListItem>
              <ToggleButtonGroup
                value={tempDeviceFilter}
                exclusive
                onChange={selectDeviceFilter}
                aria-label="Filter by device type">
                {/* 空調機 */}
                <ToggleButton
                  value="air-conditioner"
                  aria-label="Filter by air-conditioner">
                  <Box
                    className={`mySiteMainContents__subHeader--siteList__filterSettingDialog__item__toggleButton__iconBorder ${
                      tempDeviceFilter === 'air-conditioner'
                        ? 'mySiteMainContents__subHeader--siteList__filterSettingDialog__item__toggleButton__iconBorder--checked'
                        : ''
                    }`}>
                    <img
                      src={
                        tempDeviceFilter === 'air-conditioner'
                          ? iconWhite.AC_ON.src
                          : iconBlack.AC_ON.src
                      }
                      alt={`${trans(
                        `MainSiteSubHeader.image.alternativeText.acOn`
                      )}`}
                      title={`${trans(`MainSiteSubHeader.image.title.acOn`)}`}
                      className="mySiteMainContents__subHeader--siteList__filterSettingDialog__item__toggleButton__iconBorder__image"
                    />
                  </Box>
                </ToggleButton>
                {/* 冷凍機 */}
                <ToggleButton value="freezer" aria-label="Filter by freezer">
                  <Box
                    className={`mySiteMainContents__subHeader--siteList__filterSettingDialog__item__toggleButton__iconBorder ${
                      tempDeviceFilter === 'freezer'
                        ? 'mySiteMainContents__subHeader--siteList__filterSettingDialog__item__toggleButton__iconBorder--checked'
                        : ''
                    }`}>
                    <img
                      src={
                        tempDeviceFilter === 'freezer'
                          ? iconWhite.FREEZER_ON.src
                          : iconBlack.FREEZER_ON.src
                      }
                      alt={`${trans(
                        `MainSiteSubHeader.image.alternativeText.freezerOn`
                      )}`}
                      title={`${trans(
                        `MainSiteSubHeader.image.title.freezerOn`
                      )}`}
                      className="mySiteMainContents__subHeader--siteList__filterSettingDialog__item__toggleButton__iconBorder__image"
                    />
                  </Box>
                </ToggleButton>
                {/* 給湯機 */}
                <ToggleButton value="qton" aria-label="Filter by qton">
                  <Box
                    className={`mySiteMainContents__subHeader--siteList__filterSettingDialog__item__toggleButton__iconBorder ${
                      tempDeviceFilter === 'qton'
                        ? 'mySiteMainContents__subHeader--siteList__filterSettingDialog__item__toggleButton__iconBorder--checked'
                        : ''
                    }`}>
                    <img
                      src={
                        tempDeviceFilter === 'qton'
                          ? iconWhite.HOT_WATER_ON.src
                          : iconBlack.HOT_WATER_ON.src
                      }
                      alt={`${trans(
                        `MainSiteSubHeader.image.alternativeText.hotWaterOn`
                      )}`}
                      title={`${trans(
                        `MainSiteSubHeader.image.title.hotWaterOn`
                      )}`}
                      className="mySiteMainContents__subHeader--siteList__filterSettingDialog__item__toggleButton__iconBorder__image"
                    />
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </ListItem>
            {/* 権限フィルター */}
            <ListItem>
              <ListItemText
                primary={
                  <Box className="mySiteMainContents__subHeader--siteList__filterSettingDialog__item__title">
                    {trans(
                      'MainSiteSubHeader.dialog.contents.filterUserRights'
                    )}
                  </Box>
                }
              />
            </ListItem>
            <ListItem>
              <TextFieldSelectBox
                choices={pullDownMenu.SITE_AUTHORITY_FILTER}
                setValue={setTempAuthorityFilter}
                default={tempAuthorityFilter}
                class="mySiteMainContents__subHeader--siteList__filterSettingDialog__item__selectBox--filerByUserRights"
                id="selectSiteAuthorityFilter"
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            onClick={handleClose}
            buttonText={trans('MainSiteSubHeader.dialog.buttonTxt.cancel')}
            id="mv-playerSettingsCancelBtn"
          />
          <PrimaryButton
            onClick={filterApplied}
            buttonText={trans('MainSiteSubHeader.dialog.buttonTxt.ok')}
            id="mv-playerSettingsOkBtn"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
export default MainSiteSubHeader;
